import React, { useMemo, useState } from "react";
import { Alert, Button, Card, Checkbox, Form, Input, Space, Typography } from "antd";
import { LockOutlined, SafetyCertificateOutlined, UserOutlined } from "@ant-design/icons";
import routeAuth from "./routes";

import "./styles.scss";
import { postRequest } from "../../services/requestController";
import { useDispatch, useSelector } from "react-redux";
import { setToken } from "../../redux/actions";
import { Redirect } from "react-router-dom";
import { routeAdmin } from "../AdminPage";

const { Title, Text } = Typography;

const toErrors = (res) => {
    const list = res?.error?.description;
    if (Array.isArray(list) && list.length) return list.map((x) => (typeof x === "string" ? x : String(x)));
    const msg = res?.error?.message;
    if (typeof msg === "string" && msg.trim()) return [msg.trim()];
    return ["Не удалось выполнить вход. Проверьте логин и пароль."];
};

const AuthPage = () => {
    const dispatch = useDispatch();
    const token = useSelector((state) => state.token);

    const [errors, setErrors] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const successAuth = useMemo(() => token !== null, [token]);

    const onFinish = async (values) => {
        setErrors([]);
        setSubmitting(true);
        try {
            const authResponse = await postRequest("/api/admin/auth/login", values);

            if (authResponse?.success) {
                dispatch(setToken(authResponse?.data?.token));
                return;
            }

            setErrors(toErrors(authResponse));
        } catch {
            setErrors(["Сетевая ошибка. Попробуйте ещё раз."]);
        } finally {
            setSubmitting(false);
        }
    };

    if (successAuth) {
        return <Redirect to={routeAdmin()} />;
    }

    return (
        <section className="auth auth--v2">
            <div className="auth-bg">
                <div className="auth-bg__blob auth-bg__blob--a" />
                <div className="auth-bg__blob auth-bg__blob--b" />
                <div className="auth-bg__grid" />
            </div>

            <div className="container">
                <div className="auth-layout">
                    <div className="auth-hero">
                        <Space direction="vertical" size={10} style={{ width: "100%" }}>
                            <Space size={10} align="center">
                                <div className="auth-badge">
                                    <SafetyCertificateOutlined />
                                </div>
                                <Text className="auth-kicker">Admin Console</Text>
                            </Space>

                            <Title level={2} style={{ margin: 0 }}>
                                Вход в панель управления
                            </Title>
                        </Space>
                    </div>

                    <div className="auth-card">
                        <Card bordered={false} className="auth-card__inner">
                            <Space direction="vertical" size={14} style={{ width: "100%" }}>
                                <Space direction="vertical" size={2} style={{ width: "100%" }}>
                                    <Title level={4} style={{ margin: 0 }}>
                                        Авторизация
                                    </Title>
                                    <Text type="secondary">Введите логин и пароль администратора</Text>
                                </Space>

                                {errors.length > 0 ? (
                                    <Alert
                                        type="error"
                                        showIcon
                                        message="Ошибка входа"
                                        description={
                                            <Space direction="vertical" size={4}>
                                                {errors.map((e, idx) => (
                                                    <div key={idx}>{e}</div>
                                                ))}
                                            </Space>
                                        }
                                    />
                                ) : null}

                                <Form
                                    name="auth"
                                    layout="vertical"
                                    initialValues={{ remember: true }}
                                    onFinish={onFinish}
                                    autoComplete="off"
                                >
                                    <Form.Item
                                        label="Логин"
                                        name="login"
                                        rules={[{ required: true, message: "Заполните поле логин" }]}
                                    >
                                        <Input
                                            size="large"
                                            prefix={<UserOutlined />}
                                            placeholder="Введите логин"
                                            onChange={() => errors.length && setErrors([])}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label="Пароль"
                                        name="password"
                                        rules={[{ required: true, message: "Заполните поле пароль" }]}
                                    >
                                        <Input.Password
                                            size="large"
                                            prefix={<LockOutlined />}
                                            placeholder="Введите пароль"
                                            visibilityToggle={{ visible: showPassword, onVisibleChange: setShowPassword }}
                                            onChange={() => errors.length && setErrors([])}
                                        />
                                    </Form.Item>

                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        size="large"
                                        block
                                        loading={submitting}
                                        disabled={submitting}
                                    >
                                        Войти
                                    </Button>
                                </Form>

                                <div className="auth-footer">
                                    <Text type="secondary">Если вход не проходит — проверьте права доступа или статус сервера.</Text>
                                </div>
                            </Space>
                        </Card>
                    </div>
                </div>
            </div>
        </section>
    );
};

const safeStr = (v) => (typeof v === "string" ? v : v === null || v === undefined ? "" : String(v));

export { routeAuth };

export default AuthPage;